import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Sparkles, Wand2, FileText, Maximize2, Minimize2, X, AlertTriangle, Check, RotateCcw, ArrowDownToLine, Replace } from "lucide-react";

/**
 * AI body assistant — 4 actions with preview & versioning.
 * Props:
 *   getPayload():  () => {title, summary, content, source_url, source_name, editor_notes, category}
 *   onApply(html): applies the generated HTML into the editor's content field
 *   onPushVersion(prevHtml, newHtml, action): records a version snapshot in parent state
 */
export default function AIBodyAssistant({ getPayload, onApply, onPushVersion }) {
  const [busy, setBusy] = useState(null); // "body_generate" | "body_improve" | ...
  const [preview, setPreview] = useState(null); // {body, warnings, action}

  const ACTIONS = [
    { key: "body_generate", label: "Redactar cuerpo", icon: Wand2, need_body: false, help: "Genera el cuerpo desde cero usando título, bajada y notas." },
    { key: "body_improve",  label: "Mejorar redacción", icon: Sparkles, need_body: true, help: "Mejora claridad y sintaxis sin cambiar hechos." },
    { key: "body_summarize", label: "Resumir", icon: Minimize2, need_body: true, help: "Acorta manteniendo hecho principal, nombres y cifras." },
    { key: "body_expand", label: "Ampliar", icon: Maximize2, need_body: true, help: "Desarrolla mejor la redacción sin inventar nuevos hechos." },
  ];

  const stripHtml = (h) => (h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  const run = async (action) => {
    if (busy) return;
    const p = getPayload() || {};
    const needBody = ACTIONS.find((a) => a.key === action)?.need_body;
    if (needBody && !stripHtml(p.content)) {
      toast.error("No hay cuerpo cargado. Usá 'Redactar cuerpo' primero.");
      return;
    }
    setBusy(action);
    try {
      const res = await api.adminAiAssist({
        task: action,
        title: p.title || "",
        summary: p.summary || "",
        content: p.content || "",
        category: p.category || "",
        source_url: p.source_url || "",
        source_name: p.source_name || "",
        editor_notes: p.editor_notes || "",
      });
      const result = res?.result || {};
      if (!result.body) {
        toast.error("La IA no devolvió cuerpo");
        return;
      }
      setPreview({ body: result.body, warnings: result.warnings || [], action });
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const detail = e?.response?.data?.detail;
      let msg;
      if (isTimeout) {
        msg = "La IA tardó demasiado. Volvé a intentar en unos segundos.";
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        msg = detail.message || "Error al generar. Reintentar.";
      } else {
        msg = "Error al generar. Reintentar.";
      }
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const applyReplace = () => {
    if (!preview) return;
    const p = getPayload() || {};
    if (stripHtml(p.content) && !confirm("¿Reemplazar el cuerpo actual con la versión de la IA?")) return;
    onPushVersion?.(p.content || "", preview.body, preview.action);
    onApply?.(preview.body);
    toast.success("Cuerpo reemplazado. Podés restaurar la versión anterior desde el historial.");
    setPreview(null);
  };

  const applyInsertBelow = () => {
    if (!preview) return;
    const p = getPayload() || {};
    const combined = (p.content || "") + "\n" + preview.body;
    onPushVersion?.(p.content || "", combined, preview.action);
    onApply?.(combined);
    toast.success("Versión IA insertada debajo del cuerpo actual.");
    setPreview(null);
  };

  const regenerate = () => {
    const a = preview?.action;
    setPreview(null);
    if (a) run(a);
  };

  return (
    <div className="border border-softblue p-3 bg-lightgray/40 space-y-2">
      <div className="text-[10px] uppercase tracking-widest font-bold text-nightblue">Asistente IA — Cuerpo de la nota</div>
      <div className="flex flex-wrap gap-2">
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const isBusy = busy === a.key;
          return (
            <Button
              key={a.key}
              type="button"
              onClick={() => run(a.key)}
              disabled={!!busy}
              data-testid={`ai-body-${a.key}`}
              variant="outline"
              className="h-8 rounded-none border-softblue text-nightblue hover:bg-lightgray text-[10px] uppercase tracking-widest font-semibold"
              title={a.help}
            >
              {isBusy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generando…</> : <><Icon className="w-3 h-3 mr-1" /> {a.label}</>}
            </Button>
          );
        })}
      </div>
      <div className="text-[10px] text-textgray">
        Requiere confirmación explícita para reemplazar el cuerpo. La versión anterior queda guardada en el historial.
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-softblue px-5 py-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-turquoise font-bold">Versión sugerida por IA</div>
                <div className="font-serif text-lg font-bold text-blueblack">{ACTIONS.find((a) => a.key === preview.action)?.label}</div>
              </div>
              <button data-testid="ai-body-close" onClick={() => setPreview(null)} className="w-9 h-9 hover:bg-lightgray flex items-center justify-center">
                <X className="w-5 h-5 text-blueblack" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {preview.warnings.length > 0 && (
                <div className="border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900">
                  <div className="font-bold flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Advertencias / datos faltantes</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <div
                className="border border-softblue p-4 prose max-w-none max-h-[50vh] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: preview.body }}
              />
              <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-softblue">
                <Button data-testid="ai-body-apply-replace" onClick={applyReplace} className="h-9 bg-turquoise hover:bg-nightblue text-white rounded-none text-[11px] uppercase tracking-widest font-bold">
                  <Replace className="w-3.5 h-3.5 mr-1.5" /> Reemplazar cuerpo
                </Button>
                <Button data-testid="ai-body-apply-below" onClick={applyInsertBelow} variant="outline" className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold">
                  <ArrowDownToLine className="w-3.5 h-3.5 mr-1.5" /> Insertar debajo
                </Button>
                <Button data-testid="ai-body-regenerate" onClick={regenerate} variant="outline" className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Volver a generar
                </Button>
                <Button data-testid="ai-body-cancel" onClick={() => setPreview(null)} variant="outline" className="h-9 rounded-none border-softblue text-textgray text-[11px] uppercase tracking-widest font-semibold">
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
